import Modal from 'react-responsive-modal';
import CloseModal from '../svg/CloseModal';
import { Container } from '@material-ui/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { I18n } from 'react-redux-i18n';
import { CSVLink } from 'react-csv';
import ImgCircleCross from '../img/cross-circle-blue1.png';
import ImgSearch from '../img/search-icon.png';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@material-ui/core';
import CallApi from '../../../utils/CallApi';
import priceFormatting from '../../../utils/priceFormatting';
import PaginatedTable from '../../../components/PaginatedTable/PaginatedTable';
import CustomerNotesDetails from './CustomerNotesDetails';

const PAGE_SIZE = 50;

function ModalEmails({ openModal, onClose, customerIds = [], isSuite = false }) {
  const [loading, setLoading] = useState(false);
	const [pdfSelection, setPdfSelection] = useState(null);
	const [selectedCustomer, setSelectedCustomer] = useState('all');
	const [search, setSearch] = useState('');
	const [rows, setRows] = useState([])
	const [sentDateStart, setSentDateStart] = useState('');
	const [sentDateEnd, setSentDateEnd] = useState('');
	const [invoiceDateStart, setInvoiceDateStart] = useState('');
	const [invoiceDateEnd, setInvoiceDateEnd] = useState('');
	const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

	const columns = [
		{ label: I18n.t('customerAccount.sendingDate'), accessor: 'sendingDate' },
		{ label: I18n.t('customerAccount.firstAndLastName'), accessor: 'fullName' },
		{ label: I18n.t('customerAccount.email'), accessor: 'email' },
		{ label: `${I18n.t('customerAccount.invoiceDate')} *`, accessor: 'invoiceDate' },
		{ label: I18n.t('customerAccount.amount'), accessor: 'amount' },
		{ label: I18n.t('customerAccount.status'), accessor: 'status' },
	];

	const csvHeaders = columns.map(({ label, accessor }) => ({
		label: label.replace(/ \*$/, ''),
		key: accessor,
	}));

	const statusLabels = {
		sent:  I18n.t('customerAccount.sent'),
	}

	useEffect(() => {
		if (!openModal) return;

		const fetchEmails = async () => {
			try {
				const response = await CallApi(
					'PUT',
					'customer-account/getInvoiceEmails',
					'',
					{ ids: customerIds }
				);

				const formatted = formattedData(response);
				setRows(formatted);
			} catch (e) {
				console.error(e);
			}
		};

		fetchEmails();
	}, [openModal]);

	// Parse the invoice `data` JSON. Older rows only have { ids }, so the 4 toggle
	// flags default to false when absent (or when the JSON is missing/malformed).
	const parseInvoiceData = (rawData) => {
		try {
			const parsed = rawData ? JSON.parse(rawData) : null;
			return {
				ids: parsed && Array.isArray(parsed.ids) ? parsed.ids : [],
				formatA4: !!(parsed && parsed.formatA4),
				dayByDay: !!(parsed && parsed.dayByDay),
				sendSeparateNote: !!(parsed && parsed.sendSeparateNote),
				dayByDayEmail: !!(parsed && parsed.dayByDayEmail),
			};
		} catch (e) {
			return {
				ids: [],
				formatA4: false,
				dayByDay: false,
				sendSeparateNote: false,
				dayByDayEmail: false,
			};
		}
	};

	const formattedData = (data) => {
		return data
			.flat()
			.map((item) => {
				const invoice = parseInvoiceData(item.data);
				return {
					id: item.id,
					customerId: item.id_contact,
					ticketIds: invoice.ids,
					pdfOptions: {
						formatA4: invoice.formatA4,
						dayByDay: invoice.dayByDay,
						sendSeparateNote: invoice.sendSeparateNote,
						dayByDayEmail: invoice.dayByDayEmail,
					},
					dbName: item.dbName,
					firstName: item.firstname,
					lastName: item.lastname,
					sentTimestamp: item.sent_timestamp,
					invoiceTimestamp: item.last_invoice_timestamp,
					sendingDate: new Date(item.sent_timestamp * 1000).toLocaleString('fr-FR'),
					fullName: `${item.lastname ?? ''} ${item.firstname ?? ''}`.trim(),
					email: item.email,
					amount: item.amount,
					invoiceDate: new Date(item.last_invoice_timestamp * 1000).toLocaleDateString('fr-FR'),
					amount: (item.amount).toFixed(2),
					status: statusLabels[item.status] ?? item.status,
				};
			});
	}

	const handleResetClick = (e) => {
		e.preventDefault();
		setSearch('');
	};

	// input[type=date] gives "YYYY-MM-DD"; turn the bounds into inclusive unix seconds
	const toStartOfDayTs = (value) => value ? Math.floor(new Date(`${value}T00:00:00`).getTime() / 1000) : null;
	const toEndOfDayTs = (value) => value ? Math.floor(new Date(`${value}T23:59:59`).getTime() / 1000) : null;

	const filteredRows = useMemo(() => {
		const cleanSearch = search.toLowerCase();
		const sentStartTs = toStartOfDayTs(sentDateStart);
		const sentEndTs = toEndOfDayTs(sentDateEnd);
		const invoiceStartTs = toStartOfDayTs(invoiceDateStart);
		const invoiceEndTs = toEndOfDayTs(invoiceDateEnd);

		return rows.filter((row) => {
			const matchesSearch =
				!search ||
				row.fullName?.toLowerCase().includes(cleanSearch) ||
				row.email?.toLowerCase().includes(cleanSearch) ||
				row.status?.toLowerCase().includes(cleanSearch);

			const matchesSentDate =
				(!sentStartTs || row.sentTimestamp >= sentStartTs) &&
				(!sentEndTs || row.sentTimestamp <= sentEndTs);

			const matchesInvoiceDate =
				(!invoiceStartTs || row.invoiceTimestamp >= invoiceStartTs) &&
				(!invoiceEndTs || row.invoiceTimestamp <= invoiceEndTs);

			return matchesSearch && matchesSentDate && matchesInvoiceDate;
		});
	}, [rows, search, sentDateStart, sentDateEnd, invoiceDateStart, invoiceDateEnd]);

	// reset how many rows we reveal whenever the filtered set changes
	useEffect(() => {
		setVisibleCount(PAGE_SIZE);
	}, [search, sentDateStart, sentDateEnd, invoiceDateStart, invoiceDateEnd]);

	const displayedRows = useMemo(
		() => filteredRows.slice(0, visibleCount),
		[filteredRows, visibleCount]
	);

	const hasMoreRows = filteredRows.length > displayedRows.length;

	const handleShowMore = () => {
		setVisibleCount((count) => count + PAGE_SIZE);
	};

	// Click a history row -> reopen the interactive PDF panel (Détaillé / Non détaillé /
	// Envoyer + toggles + preview + Exporter), pre-loaded with the stored ticket ids,
	// exactly as if they had been re-selected manually.
	const handleRowClick = (row) => {
		if (!row.ticketIds || !row.ticketIds.length) return;
		const listPaymentIds = row.ticketIds.map((id) => ({
			idPayment: id,
			customerId: row.customerId,
			userDb: row.dbName,
		}));
		setPdfSelection({
			rowId: row.id,
			listPaymentIds,
			customerId: row.customerId,
			customerFirstName: row.firstName,
			customerLastName: row.lastName,
			customerEmail: row.email,
			pdfOptions: row.pdfOptions,
			// le renvoi ne recharge pas les notes (dataNotes={{}} plus bas) : on garde
			// le montant et la date de la ligne pour reecrire l'historique
			amount: row.amount,
			invoiceTimestamp: row.invoiceTimestamp,
		});
	};

	const closePdfSelection = () => setPdfSelection(null);

  return (
    <>
    <Modal
      classNames={{
				modal: 'export_history_modal',
				overlay: 'modal_export_history_overlay emails_modal',
			}}
			open={openModal}
			showCloseIcon={false}
			closeOnOverlayClick={true}
			onClose={() => {
				if (loading) return false;
				onClose();
			}}
    >
      <div className="export_modal_content">
        <div
					className="close_icon"
					onClick={e => {
						if (loading) return false;
						onClose();
					}}>
					<CloseModal />
				</div>
        <h3>{I18n.t('customerAccount.invoiceEmailsHistoric')}</h3>
        <p style={{ fontSize: 13, fontStyle: 'italic', color: '#888', margin: '0 0 10px 15px' }}>
					{I18n.t('customerAccount.invoiceEmailsHistoricSubtitle')}
				</p>
        <div className="emails_modal_main">
          <Container maxWidth="lg">
						<div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 12 }}>
							<div>
								<p style={{ fontSize: 12, color: '#555', margin: '0 0 4px' }}>
									{I18n.t('customerAccount.sendingDate')}
								</p>
								<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
									<input
										type="date"
										value={sentDateStart}
										max={sentDateEnd || undefined}
										onChange={(e) => setSentDateStart(e.target.value)}
									/>
									<span style={{ fontSize: 12, color: '#888' }}>{I18n.t('customerAccount.dateRangeTo')}</span>
									<input
										type="date"
										value={sentDateEnd}
										min={sentDateStart || undefined}
										onChange={(e) => setSentDateEnd(e.target.value)}
									/>
								</div>
							</div>
							<div>
								<p style={{ fontSize: 12, color: '#555', margin: '0 0 4px' }}>
									{I18n.t('customerAccount.invoiceDate')}
								</p>
								<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
									<input
										type="date"
										value={invoiceDateStart}
										max={invoiceDateEnd || undefined}
										onChange={(e) => setInvoiceDateStart(e.target.value)}
									/>
									<span style={{ fontSize: 12, color: '#888' }}>{I18n.t('customerAccount.dateRangeTo')}</span>
									<input
										type="date"
										value={invoiceDateEnd}
										min={invoiceDateStart || undefined}
										onChange={(e) => setInvoiceDateEnd(e.target.value)}
									/>
								</div>
							</div>
						</div>
						<div className="table-filters" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
							<div className="search-bar-emails">
								{search ? (
									<img
										src={ImgCircleCross}
										onClick={handleResetClick}
										className="remove"
										alt="img remove"
									/>
								) : (
									''
								)}
								<input
									type="text"
									placeholder={I18n.t('customerAccount.search')}
									value={search ? search : ''}
									onChange={(e) => setSearch(e.target.value)}
								/>
								<div>
									<img src={ImgSearch} alt="Search" className="search-img" />
								</div>
							</div>
							<CSVLink
								data={filteredRows}
								headers={csvHeaders}
								filename="historique-emails-factures.csv"
								separator=";"
								onClick={(e) => {
									if (!filteredRows.length) e.preventDefault();
								}}
							>
								<button
									type="button"
									disabled={!filteredRows.length}
									style={{
										background: filteredRows.length ? '#262957' : '#ccc',
										color: '#fff',
										border: 'none',
										borderRadius: 20,
										padding: '8px 16px',
										fontSize: 13,
										cursor: filteredRows.length ? 'pointer' : 'not-allowed',
									}}
								>
									{I18n.t('customerAccount.downloadCsv')}
								</button>
							</CSVLink>
						</div>
						<PaginatedTable
							columns={columns}
							filteredRows={displayedRows}
							pageSize={10}
							defaultSort={{
								key: 'sendingDate',
								direction: 'desc',
							}}
							onRowClick={handleRowClick}
						/>
						{hasMoreRows && (
							<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
								<p style={{ fontSize: 12, fontStyle: 'italic', color: '#e84249', margin: 0 }}>
									{`${I18n.t('customerAccount.partialResultsWarning')} (${displayedRows.length}/${filteredRows.length})`}
								</p>
								<button
									type="button"
									onClick={handleShowMore}
									style={{
										background: 'transparent',
										color: '#262957',
										border: '1px solid #262957',
										borderRadius: 20,
										padding: '6px 14px',
										fontSize: 12,
										cursor: 'pointer',
									}}
								>
									{I18n.t('customerAccount.showMoreResults')}
								</button>
							</div>
						)}
						<p style={{ fontSize: 12, fontStyle: 'italic', color: '#888', marginTop: 8 }}>
							{I18n.t('customerAccount.invoiceDateDisclaimer')}
						</p>
          </Container>
        </div>
      </div>
    </Modal>

    {pdfSelection && (
      <Modal
        classNames={{
          modal: 'export_history_modal invoice-preview-modal',
          overlay: 'modal_export_history_overlay',
        }}
        open={true}
        showCloseIcon={false}
        closeOnOverlayClick={true}
        onClose={closePdfSelection}
      >
        <div className="export_modal_content">
          <div className="close_icon" onClick={closePdfSelection}>
            <CloseModal />
          </div>
          <h3>{I18n.t('customerAccount.invoicePreviewTitle')}</h3>
          <div className="pdf-bloc invoice-preview-panel">
            <CustomerNotesDetails
              key={pdfSelection.rowId}
              listPaymentIds={pdfSelection.listPaymentIds}
              filter="paid"
              creditDetails={{}}
              paymentTypes={[]}
              language={window.navigator.language}
              dataNotes={{}}
              emptyList={false}
              selectedCompany="all"
              customerId={pdfSelection.customerId}
              customerFirstName={pdfSelection.customerFirstName}
              customerLastName={pdfSelection.customerLastName}
              customerEmail={pdfSelection.customerEmail}
              resendAmount={pdfSelection.amount}
              resendInvoiceTimestamp={pdfSelection.invoiceTimestamp}
              companies={[]}
              isSuite={isSuite}
              isResend={true}
              initialFormatA4={pdfSelection.pdfOptions ? pdfSelection.pdfOptions.formatA4 : false}
              initialDayByDay={pdfSelection.pdfOptions ? pdfSelection.pdfOptions.dayByDay : false}
              initialSendSeparateNote={pdfSelection.pdfOptions ? pdfSelection.pdfOptions.sendSeparateNote : false}
              initialDayByDayEmail={pdfSelection.pdfOptions ? pdfSelection.pdfOptions.dayByDayEmail : false}
              onUpdateLinkPdf={() => {}}
              onUpdateGenerationPdf={() => {}}
            />
          </div>
        </div>
      </Modal>
    )}
    </>
  )
}

export default ModalEmails;